import React, { useEffect, useMemo, useState } from "react";

const ANDROID_URL =
    "https://play.google.com/store/apps/details?id=com.anonymous.goshopadmin";

const IOS_URL =
    "https://apps.apple.com/bj/app/goshop-b2b/id6761507630?l=fr-FR";

function detectDevice() {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(ua)) return "android";
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
    return "other";
}

function StoreButton({ type, href }) {
    const isApple = type === "apple";

    const baseStyle = {
        display: "inline-flex",
        alignItems: "center",
        gap: "14px",
        textDecoration: "none",
        background: "#000",
        color: "#fff",
        borderRadius: "18px",
        padding: "14px 18px",
        minWidth: "250px",
        justifyContent: "flex-start",
        boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
    };

    const iconWrapStyle = {
        width: "42px",
        height: "42px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    };

    const smallTextStyle = {
        fontSize: "0.72rem",
        opacity: 0.9,
        lineHeight: 1.1,
        marginBottom: "2px",
    };

    const bigTextStyle = {
        fontSize: "1.15rem",
        fontWeight: 800,
        lineHeight: 1.1,
        letterSpacing: "0.2px",
    };

    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            style={baseStyle}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 16px 28px rgba(0,0,0,0.22)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.18)";
            }}
        >
            <div style={iconWrapStyle}>
                {isApple ? (
                    <svg
                        width="34"
                        height="34"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path d="M16.365 1.43c0 1.14-.465 2.26-1.167 3.028-.764.83-2.02 1.47-3.148 1.38-.14-1.08.39-2.22 1.11-2.99.8-.86 2.1-1.48 3.205-1.418zM20.94 17.04c-.52 1.2-.77 1.73-1.44 2.77-.94 1.46-2.26 3.28-3.9 3.29-1.46.01-1.84-.95-3.82-.94-1.98.01-2.4.96-3.86.95-1.64-.02-2.89-1.66-3.83-3.12-2.63-4.06-2.9-8.82-1.28-11.31 1.15-1.77 2.97-2.8 4.68-2.8 1.74 0 2.84.96 4.28.96 1.4 0 2.25-.96 4.27-.96 1.52 0 3.13.83 4.28 2.27-3.76 2.06-3.15 7.42.62 8.89z" />
                    </svg>
                ) : (
                    <svg
                        width="34"
                        height="34"
                        viewBox="0 0 512 512"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path d="M325.3 234.3L104.6 13.6l174.2 301.7 46.5-81z" fill="#34A853" />
                        <path d="M104.6 13.6A27.7 27.7 0 0096 32v448c0 7.2 2.9 13.8 8 18.7l174.8-183.4L104.6 13.6z" fill="#4285F4" />
                        <path d="M416.6 280.5l-91.3-46.2-46.5 81 46.5 80.8 91-46c15.3-7.7 15.4-29.4.3-37.1z" fill="#FBBC04" />
                        <path d="M278.8 315.3L104 498.7c7 6.5 17.4 8 26.1 3.6l195.2-98-46.5-89z" fill="#EA4335" />
                    </svg>
                )}
            </div>

            <div style={{ textAlign: "left" }}>
                <div style={smallTextStyle}>
                    {isApple ? "Télécharger sur" : "DISPONIBLE SUR"}
                </div>
                <div style={bigTextStyle}>
                    {isApple ? "App Store" : "Google Play"}
                </div>
            </div>
        </a>
    );
}

export default function DownloadApp() {
    const [countdown, setCountdown] = useState(2);

    const device = useMemo(() => detectDevice(), []);

    const targetUrl = useMemo(() => {
        if (device === "android") return ANDROID_URL;
        if (device === "ios") return IOS_URL;
        return null;
    }, [device]);

    useEffect(() => {
        if (!targetUrl) return;

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    window.location.replace(targetUrl);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [targetUrl]);

    const pageStyle = {
        minHeight: "100vh",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 45%, #eef8f2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily:
            "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    };

    const cardStyle = {
        width: "100%",
        maxWidth: "760px",
        background: "#fff",
        borderRadius: "30px",
        boxShadow: "0 20px 55px rgba(0,0,0,0.08)",
        padding: "38px 24px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
    };

    const glowStyle = {
        position: "absolute",
        width: "260px",
        height: "260px",
        borderRadius: "999px",
        background: "rgba(34,197,94,0.08)",
        top: "-90px",
        right: "-60px",
        filter: "blur(8px)",
    };

    const badgeStyle = {
        display: "inline-block",
        background: "#ecfdf3",
        color: "#027a48",
        borderRadius: "999px",
        padding: "8px 14px",
        fontWeight: 700,
        fontSize: "0.9rem",
        marginBottom: "16px",
        position: "relative",
        zIndex: 1,
    };

    const titleStyle = {
        fontSize: "clamp(2rem, 4vw, 3rem)",
        lineHeight: 1.08,
        fontWeight: 900,
        color: "#111827",
        margin: "0 0 14px",
        position: "relative",
        zIndex: 1,
    };

    const textStyle = {
        maxWidth: "560px",
        margin: "0 auto 22px",
        color: "#4b5563",
        fontSize: "1.04rem",
        lineHeight: 1.7,
        position: "relative",
        zIndex: 1,
    };

    const infoStyle = {
        display: "inline-block",
        background: "#f9fafb",
        color: "#374151",
        borderRadius: "14px",
        padding: "12px 16px",
        fontWeight: 600,
        fontSize: "0.97rem",
        marginBottom: "26px",
        position: "relative",
        zIndex: 1,
    };

    const storesWrapStyle = {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "16px",
        position: "relative",
        zIndex: 1,
    };

    const noteStyle = {
        marginTop: "18px",
        color: "#6b7280",
        fontSize: "0.95rem",
        position: "relative",
        zIndex: 1,
    };

    const logoBoxStyle = {
        width: "88px",
        height: "88px",
        margin: "0 auto 18px",
        borderRadius: "24px",
        background: "linear-gradient(135deg, #16a34a, #111827)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 900,
        fontSize: "1.8rem",
        boxShadow: "0 16px 30px rgba(0,0,0,0.16)",
        position: "relative",
        zIndex: 1,
    };

    const logoStyle = {
        width: "88px",
        height: "88px",
        objectFit: "cover",
        borderRadius: "24px",
        margin: "0 auto 18px",
        display: "block",
        position: "relative",
        zIndex: 1,
        boxShadow: "0 16px 30px rgba(0,0,0,0.16)",
        background: "#fff",
    };

    const deviceText =
        device === "android"
            ? `Android détecté — redirection dans ${countdown}s`
            : device === "ios"
                ? `iPhone / iPad détecté — redirection dans ${countdown}s`
                : "Choisissez votre plateforme de téléchargement";

    return (
        <div style={pageStyle}>
            <div style={cardStyle}>
                <div style={glowStyle} />
                <img
                    src="/icon.png"
                    alt="GoShop"
                    style={logoStyle}
                />

                <div style={badgeStyle}>GoShop App</div>

                <h1 style={titleStyle}>
                    Téléchargez GoShop
                    <br />
                    en toute simplicité
                </h1>

                <p style={textStyle}>
                    Achetez rapidement, échangez avec le vendeur, négociez si besoin et
                    finalisez vos commandes facilement depuis votre téléphone.
                </p>

                <div style={infoStyle}>{deviceText}</div>

                <div style={storesWrapStyle}>
                    <StoreButton type="apple" href={IOS_URL} />
                    <StoreButton type="google" href={ANDROID_URL} />
                </div>

                <p style={noteStyle}>
                    Si la redirection automatique ne se lance pas, utilisez l’un des
                    boutons ci-dessus.
                </p>
            </div>
        </div>
    );
}